import React from 'react';
import PropTypes from 'prop-types';
import { Box, Text, RadioButton } from 'grommet';
import { useAppTheme } from '@Components/Context';
import { OnboardingTermsSource } from '@RedflagAI/node-utility/interface';
import { AppButton } from '@Components/Control';
import { downloadAgreement, generateAgreementHtml } from '@Components/Modal/agreementHtmlGenerator';

const MAX_FILE_BYTES = 200000;

const DOWNLOAD_FORMATS = [
  { label: 'HTML', value: 'html' },
  { label: 'TXT', value: 'txt' },
  { label: 'PDF', value: 'pdf' },
  { label: 'DOCX', value: 'docx' },
];

// Strip font-family declarations so pdfmake never encounters an unregistered font
// (e.g. "Aptos" from Word-exported HTML) when generating the signed PDF copy.
const sanitizeHtmlFonts = (html) => html.replace(/font-family\s*:[^;}]+;?/gi, '');

// Toolbar button for the rich text editor
const ToolbarBtn = ({
  label, title, onClick, active = false, theme,
}) => (
  <button
    type="button"
    title={title}
    onMouseDown={(e) => { e.preventDefault(); onClick(); }}
    style={{
      background: active ? theme.button.selectBg : 'transparent',
      border: `1px solid ${theme.page.border}`,
      borderRadius: '4px',
      color: theme.page.primaryText,
      cursor: 'pointer',
      fontWeight: 600,
      fontSize: '0.8rem',
      padding: '0.2rem 0.5rem',
      lineHeight: 1.4,
    }}
  >
    {label}
  </button>
);

ToolbarBtn.propTypes = {
  label: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  onClick: PropTypes.func.isRequired,
  active: PropTypes.bool,
  theme: PropTypes.object.isRequired,
};

const RichTextEditor = ({ onSave, initialHtml, theme }) => {
  const { page, button } = theme;
  const editorRef = React.useRef(null);
  const [saved, setSaved] = React.useState(false);

  React.useEffect(() => {
    if (editorRef.current && initialHtml) {
      // Use DOMParser to extract only the body content so that any <style> or
      // <head> tags in a full HTML document don't leak into the live page DOM.
      const parsed = new DOMParser().parseFromString(initialHtml, 'text/html');
      editorRef.current.innerHTML = parsed.body.innerHTML || initialHtml;
    }
  }, []);

  // eslint-disable-next-line no-undef
  const exec = (cmd, value = null) => {
    editorRef.current?.focus();
    // execCommand is deprecated but remains the only no-dependency way
    // to do inline rich-text editing in a contentEditable div.
    document.execCommand(cmd, false, value);
  };

  const encodeAndSave = (html, showConfirmation = false) => {
    if (!html || html === '<br>') return;
    const bytes = new TextEncoder().encode(sanitizeHtmlFonts(html));
    let binary = '';
    bytes.forEach((b) => { binary += String.fromCharCode(b); });
    onSave(btoa(binary));
    if (showConfirmation) {
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    }
  };

  // Persist content to parent state on every edit so navigating away never loses work.
  const handleInput = (e) => {
    setSaved(false);
    encodeAndSave(e.currentTarget.innerHTML);
  };

  const handleSave = () => {
    encodeAndSave(editorRef.current?.innerHTML?.trim() || '', true);
  };

  return (
    <Box gap="0.5rem">
      {/* Toolbar */}
      <Box
        direction="row"
        gap="0.3rem"
        wrap
        pad={{ horizontal: '0.5rem', vertical: '0.35rem' }}
        round={{ corner: 'top', size: 'var(--app-radius)' }}
        border={{ color: page.border, size: '1px' }}
        background={page.altBg}
        align="center"
      >
        <ToolbarBtn label="B" title="Bold" onClick={() => exec('bold')} theme={theme} />
        <ToolbarBtn label="I" title="Italic" onClick={() => exec('italic')} theme={theme} />
        <ToolbarBtn label="U" title="Underline" onClick={() => exec('underline')} theme={theme} />
        <Box
          width="1px"
          height="18px"
          background={page.border}
          margin={{ horizontal: '0.15rem' }}
        />
        <ToolbarBtn label="H1" title="Heading 1" onClick={() => exec('formatBlock', 'h1')} theme={theme} />
        <ToolbarBtn label="H2" title="Heading 2" onClick={() => exec('formatBlock', 'h2')} theme={theme} />
        <ToolbarBtn label="H3" title="Heading 3" onClick={() => exec('formatBlock', 'h3')} theme={theme} />
        <ToolbarBtn label="H4" title="Heading 4" onClick={() => exec('formatBlock', 'h4')} theme={theme} />
        <ToolbarBtn label="H5" title="Heading 5" onClick={() => exec('formatBlock', 'h5')} theme={theme} />
        <ToolbarBtn label="H6" title="Heading 6" onClick={() => exec('formatBlock', 'h6')} theme={theme} />
        <ToolbarBtn label="P" title="Paragraph" onClick={() => exec('formatBlock', 'p')} theme={theme} />
        <Box
          width="1px"
          height="18px"
          background={page.border}
          margin={{ horizontal: '0.15rem' }}
        />
        <ToolbarBtn label="• List" title="Bullet list" onClick={() => exec('insertUnorderedList')} theme={theme} />
        <ToolbarBtn label="1. List" title="Numbered list" onClick={() => exec('insertOrderedList')} theme={theme} />
        <Box
          width="1px"
          height="18px"
          background={page.border}
          margin={{ horizontal: '0.15rem' }}
        />
        <ToolbarBtn label="Clear" title="Remove formatting" onClick={() => exec('removeFormat')} theme={theme} />
      </Box>
      {/* Editable area */}
      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        style={{
          minHeight: '350px',
          maxHeight: '800px',
          overflowY: 'auto',
          padding: '0.75rem',
          background: page.cardBg,
          color: page.primaryText,
          border: `1px solid ${page.border}`,
          borderTop: 'none',
          borderRadius: '0 0 var(--app-radius) var(--app-radius)',
          lineHeight: 1.65,
          fontSize: '0.875rem',
          outline: 'none',
          fontFamily: 'inherit',
        }}
        onInput={handleInput}
      />
      <Box direction="row" gap="0.75rem" align="center">
        <AppButton
          overrideHover
          level="tableControls"
          width="fit-content"
          bgColor={button.primaryBg}
          color={button.primaryText}
          fontWeight={600}
          label="Save content"
          onClick={handleSave}
        />
        {saved && (
          <Text size="0.8rem" color="var(--app-success)">Content saved.</Text>
        )}
      </Box>
    </Box>
  );
};

RichTextEditor.propTypes = {
  onSave: PropTypes.func.isRequired,
  initialHtml: PropTypes.string,
  theme: PropTypes.object.isRequired,
};

const Step5Terms = ({ terms, setTerms, accountType = '' }) => {
  const { theme } = useAppTheme();
  const { page, button } = theme;
  const fileInputRef = React.useRef(null);
  const [fileError, setFileError] = React.useState(null);
  const [downloadFormat, setDownloadFormat] = React.useState('html');
  const isAgency = accountType === 'agency';

  // customMode lives in terms so it survives step navigation
  const customMode = terms.customMode || 'upload';

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setFileError(null);

    if (file.size === 0) {
      setFileError('File is empty.');
      return;
    }
    if (file.size > MAX_FILE_BYTES) {
      setFileError(
        `File is too large (${(file.size / 1000).toFixed(1)} KB). Maximum is ${MAX_FILE_BYTES / 1000} KB.`,
      );
      return;
    }

    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const text = sanitizeHtmlFonts(ev.target.result);
        const fileBytes = new TextEncoder().encode(text);
        let binary = '';
        fileBytes.forEach((b) => { binary += String.fromCharCode(b); });
        setTerms((prev) => ({
          ...prev,
          bodyBase64: btoa(binary),
          uploadedFileName: file.name,
        }));
      } catch {
        setFileError('Failed to encode file. Ensure it is a valid text-based file.');
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const clearFile = () => {
    setTerms((prev) => ({ ...prev, bodyBase64: '', uploadedFileName: '' }));
    setFileError(null);
  };

  const setSource = (source) => {
    setTerms((prev) => ({
      ...prev,
      source,
      bodyBase64: '',
      uploadedFileName: '',
      customMode: 'upload',
    }));
    setFileError(null);
  };

  const switchCustomMode = (mode) => {
    if (mode === 'upload') {
      // Switching to upload clears any editor content
      setTerms((prev) => ({
        ...prev,
        customMode: mode,
        bodyBase64: '',
        uploadedFileName: '',
      }));
      setFileError(null);
    } else {
      // Switching to editor keeps existing content so a return visit restores it
      setTerms((prev) => ({ ...prev, customMode: mode }));
    }
  };

  const handleEditorSave = (base64) => {
    setTerms((prev) => ({
      ...prev,
      bodyBase64: base64,
      uploadedFileName: 'in-app-editor',
    }));
  };

  // Decode saved editor HTML for re-initialisation when switching back to editor
  const savedEditorHtml = React.useMemo(() => {
    if (!terms.bodyBase64 || terms.uploadedFileName !== 'in-app-editor') return '';
    try {
      const bytes = Uint8Array.from(atob(terms.bodyBase64), (c) => c.charCodeAt(0));
      return new TextDecoder().decode(bytes);
    } catch { return ''; }
  }, [terms.bodyBase64, terms.uploadedFileName]);

  return (
    <Box gap="1.25rem">
      <Box gap="0.75rem">
        <Text size="0.95rem" weight={700} color={page.primaryText}>
          Terms &amp; Conditions
        </Text>
        <Box direction="row" gap="2rem">
          <RadioButton
            name="termsSource"
            value={OnboardingTermsSource.DEFAULT}
            checked={terms.source === OnboardingTermsSource.DEFAULT}
            onChange={() => setSource(OnboardingTermsSource.DEFAULT)}
            label={(
              <Box>
                <Text size="0.875rem" color={page.primaryText}>Default Terms</Text>
                <Text size="0.75rem" color={page.secondaryText}>
                  Use Redflag standard agreement
                </Text>
              </Box>
            )}
          />
          <RadioButton
            name="termsSource"
            value={OnboardingTermsSource.CUSTOM}
            checked={terms.source === OnboardingTermsSource.CUSTOM}
            onChange={() => setSource(OnboardingTermsSource.CUSTOM)}
            label={(
              <Box>
                <Text size="0.875rem" color={page.primaryText}>Custom Terms</Text>
                <Text size="0.75rem" color={page.secondaryText}>
                  Upload a file or write in-app
                </Text>
              </Box>
            )}
          />
        </Box>
      </Box>
      {terms.source === OnboardingTermsSource.DEFAULT && (
        <Box
          pad="1rem"
          round="var(--app-radius)"
          background={page.altBg}
          border={{ color: page.border, size: '1px' }}
          gap="0.5rem"
        >
          <Text size="0.875rem" color={page.primaryText}>
            The customer will be presented with the standard Redflag Terms &amp; Conditions
            during onboarding. No additional upload is required.
          </Text>
        </Box>
      )}
      {terms.source === OnboardingTermsSource.CUSTOM && (
        <Box gap="0.75rem">
          {/* Sub-mode toggle */}
          <Box direction="row" gap="0.5rem">
            {[
              { value: 'upload', label: 'Upload file' },
              { value: 'editor', label: 'Write in app' },
            ].map((opt) => (
              <AppButton
                key={opt.value}
                overrideHover
                level="dynamicSmall"
                bgColor={customMode === opt.value ? button.primaryBg : page.altBg}
                color={customMode === opt.value ? 'white' : page.primaryText}
                borderColor={customMode === opt.value ? button.primaryBg : button.border}
                fontWeight={600}
                style={{ border: `1px solid ${customMode === opt.value ? button.primaryBg : button.border}` }}
                label={opt.label}
                onClick={() => switchCustomMode(opt.value)}
              />
            ))}
          </Box>
          {customMode === 'upload' && (
            <Box
              pad="1rem"
              round="var(--app-radius)"
              background={page.altBg}
              border={{ color: page.border, size: '1px' }}
              gap="0.5rem"
            >
              <Text size="0.82rem" color={page.secondaryText}>
                {`Upload a custom agreement file (.txt or .html). Maximum size: ${MAX_FILE_BYTES / 1000} KB.`}
              </Text>
              <Text size="0.78rem" color={page.secondaryText}>
                Note: only .txt and .html files will display correctly to the customer.
                Other formats (e.g. .docx) will not render inline.
              </Text>
              <input
                ref={fileInputRef}
                type="file"
                accept=".txt,.html,.htm"
                onChange={handleFileChange}
                style={{ display: 'none' }}
              />
              <Box direction="row" gap="0.75rem" align="center">
                <AppButton
                  overrideHover
                  level="dynamicSmall"
                  bgColor={button.primaryBg}
                  color="white"
                  label="Choose file"
                  onClick={() => fileInputRef.current?.click()}
                />
                {terms.uploadedFileName && terms.uploadedFileName !== 'in-app-editor' && (
                  <Box direction="row" align="center" gap="0.5rem">
                    <Text size="0.82rem" color={page.primaryText}>{terms.uploadedFileName}</Text>
                    <AppButton
                      overrideHover
                      level="dynamicSmall"
                      bgColor="transparent"
                      color="var(--app-error)"
                      label="✕ Remove"
                      onClick={clearFile}
                    />
                  </Box>
                )}
              </Box>
              {fileError && (
                <Text size="0.8rem" color="var(--app-error)">{fileError}</Text>
              )}
              {terms.bodyBase64 && !fileError && terms.uploadedFileName !== 'in-app-editor' && (
                <Text size="0.78rem" color="var(--app-success)">
                  {`File loaded: ${terms.uploadedFileName} (${Math.round((terms.bodyBase64.length * 0.75) / 1000)} KB decoded)`}
                </Text>
              )}
            </Box>
          )}
          {customMode === 'editor' && (
            <Box
              pad="1rem"
              round="var(--app-radius)"
              background={page.altBg}
              border={{ color: page.border, size: '1px' }}
              gap="0.75rem"
            >
              <Text size="0.82rem" color={page.secondaryText}>
                Write and format your custom agreement below. The content will be shown
                to the customer exactly as formatted.
              </Text>
              <RichTextEditor
                key={customMode}
                onSave={handleEditorSave}
                initialHtml={savedEditorHtml || generateAgreementHtml(new Date().getFullYear(), isAgency)}
                theme={theme}
              />
              {terms.bodyBase64 && terms.uploadedFileName === 'in-app-editor' && (
                <Text size="0.78rem" color="var(--app-success)">
                  Content saved and ready to send.
                </Text>
              )}
            </Box>
          )}
        </Box>
      )}
      {/* Download default agreement */}
      <Box
        pad="1rem"
        round="var(--app-radius)"
        background={page.altBg}
        border={{ color: page.border, size: '1px' }}
        gap="0.75rem"
      >
        <Box gap="0.2rem">
          <Text size="0.875rem" weight={700} color={page.primaryText}>
            Download Default Agreement
          </Text>
          <Text size="0.78rem" color={page.secondaryText}>
            {`Download the standard agreement${isAgency ? ' (agency version)' : ''}. Use this as a starting point for a custom agreement.`}
          </Text>
        </Box>
        <Box direction="row" gap="1.5rem" align="center" wrap>
          {DOWNLOAD_FORMATS.map((fmt) => (
            <RadioButton
              key={fmt.value}
              name="downloadFormat"
              value={fmt.value}
              checked={downloadFormat === fmt.value}
              onChange={() => setDownloadFormat(fmt.value)}
              label={<Text size="0.82rem" color={page.primaryText}>{fmt.label}</Text>}
            />
          ))}
        </Box>
        <Box>
          <AppButton
            overrideHover
            level="tableControls"
            width="fit-content"
            bgColor={button.primaryBg}
            color={button.primaryText}
            label={`Download .${downloadFormat}`}
            onClick={() => downloadAgreement(downloadFormat, isAgency)}
          />
        </Box>
      </Box>
    </Box>
  );
};

Step5Terms.propTypes = {
  terms: PropTypes.shape({
    source: PropTypes.string.isRequired,
    bodyBase64: PropTypes.string.isRequired,
    uploadedFileName: PropTypes.string.isRequired,
    customMode: PropTypes.string,
  }).isRequired,
  setTerms: PropTypes.func.isRequired,
  accountType: PropTypes.string,
};

export default Step5Terms;
